import { DestroyRef, Injectable, inject } from '@angular/core';
import { SetDynamicMercerAccess } from '@coin/shared/auth/util';
import { CommunicationTypeEnum, TodoTaskType } from '@coin/shared/core/util-enums';
import { DateOperations } from '@coin/shared/core/util-helpers';
import { Topic } from '@coin/shared/core/util-models';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { produce } from 'immer';
import { Observable, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CosmosDashboardService } from '@coin/shared/core/data-access';
import {
  ExtractCategories,
  LoadTopics,
  LoadTopicsFail,
  LoadTopicsSuccess,
  UpdateBannerVisibility
} from './banner.actions';

export interface BannerStateModel {
  bannerTopics: Topic[];
  loaded: boolean;
  loading: boolean;
}

@State<BannerStateModel>({
  name: 'bannerState',
  defaults: {
    bannerTopics: undefined,
    loaded: false,
    loading: false
  }
})
@Injectable()
export class BannerState {
  private dashboardService = inject(CosmosDashboardService);
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);

  @Selector()
  static visibleBannerTopics(state: BannerStateModel): Topic[] {
    return state.bannerTopics.filter(
      topic =>
        topic.type === CommunicationTypeEnum.Banner &&
        topic.showBanner &&
        !DateOperations.dueDateOverBasedOnDay(topic.dateEnd)
    );
  }

  @Selector()
  static loaded(state: BannerStateModel): boolean {
    return state.loaded;
  }

  @Action(LoadTopics)
  loadTopics({ patchState, dispatch }: StateContext<BannerStateModel>): Observable<void> {
    patchState({ loading: true });

    return this.dashboardService.getBannerTopics().pipe(
      switchMap(topics => dispatch(new LoadTopicsSuccess(topics))),
      catchError(error => dispatch(new LoadTopicsFail(error)))
    );
  }

  @Action(LoadTopicsSuccess)
  loadTopicsSuccess(ctx: StateContext<BannerStateModel>, { payload }: LoadTopicsSuccess): Observable<void> {
    const { bannerTopics } = ctx.getState();
    const newBannerTopics = this.mapBannerTopics(
      bannerTopics,
      payload.filter(topic => topic.type === CommunicationTypeEnum.Banner)
    );
    const topics = payload.filter(topic => topic.type !== CommunicationTypeEnum.Banner);
    ctx.patchState({ bannerTopics: newBannerTopics });

    ctx
      .dispatch(new ExtractCategories())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        ctx.patchState({ loaded: true, loading: false });
      });

    const hasFinalIncumbentTodo = topics.some(
      topic => topic.type === CommunicationTypeEnum.Task && topic.taskType === TodoTaskType.FinalIncumbent
    );
    return this.store.dispatch(new SetDynamicMercerAccess(hasFinalIncumbentTodo));
  }

  @Action(UpdateBannerVisibility)
  updateBannerVisibility(ctx: StateContext<BannerStateModel>, { payload }: UpdateBannerVisibility): void {
    ctx.setState(
      produce(ctx.getState(), state => {
        const foundIndex = state.bannerTopics.findIndex(topic => topic.id === payload.id);
        if (foundIndex >= 0) {
          state.bannerTopics[foundIndex].showBanner = false;
        }
      })
    );
  }

  @Action(LoadTopicsFail)
  loadTopicsFail({ dispatch }: StateContext<BannerStateModel>): Observable<void> {
    dispatch({ loaded: false, loading: false });
    return this.store.dispatch(new SetDynamicMercerAccess(false));
  }

  mapBannerTopics(currentTopics: Topic[], newTopics: Topic[]): Topic[] {
    return newTopics.map(topic => {
      const currentTopic = currentTopics?.find(currentTopic => currentTopic.id === topic.id);
      if (currentTopic) {
        topic.showBanner = currentTopic.showBanner;
      }
      return topic;
    });
  }
}
